import { useEffect, useState } from 'react';
import jwt_decode from 'jwt-decode';
import { DownloadTemplate, LeavesAll, PickByValue } from '../types';
import { useLocation } from 'react-router';
import Api from '../api/API';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { TemplatesStore } from '../store/Templates';
import { translationEN, translationRU } from '../components/help/translations';
import { LeaveRequestResponseI } from '../components/requestModal/types';
import { getHours, getMinutes } from 'date-fns';
import apiConfig from '../api/routes';
import { RequestsStore } from '../store/Requests';

export function groupBy<T extends ReadonlyArray<any>, I> (arr : T, key: keyof  PickByValue<I, number>):Record<number, T>{
  return arr.reduce((predicate, item: I) => {
    (predicate[item[key]]  = predicate[item[key]] || []).push(item);
    return predicate;
  }, {});
}

export const onTableRowChange = (pagination: any, filters: any, sorter: any) => {
  let currentSort: 'ASC' | 'DESC' | undefined = undefined;
  if(sorter.order === 'ascend'){
    currentSort = 'ASC';
  }
  if(sorter.order === 'descend'){
    currentSort = 'DESC';
  }
  if(!sorter.order) {
    currentSort = undefined;
  }
  return {
    columnName: sorter.column ? sorter.column.name.toLowerCase() : '',
    order: currentSort
  };

};

export function insertOneByIndex<T>(arr:T[]){
  const result:T[] = [...arr];
  return (index: number) => {
    return(item: T)=>{
      result.splice(index, 0, item);
      return result;
    };
  };
}
export const decodeToken = (): Record<string, unknown> => {
  const token = JSON.parse(localStorage.getItem('token') as string) ;
  let decode: Record<string, unknown> = {};
  if(token){
    decode = jwt_decode(token);
  }
  return decode;
};


export const  findTranslationsSelector = (currentLang: string ) => currentLang === 'en' ? translationEN : translationRU;
export const buildDownloadFile = (blob: any, docName: string) => {
  const url = window.URL.createObjectURL(
    new Blob([blob])
  );
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    `${docName !== '' ? docName : 'leave_control_report.zip'}`
  );
  document.body.appendChild(link);
  link.click();
  link?.parentNode?.removeChild(link);
};

export const toHumanCase = (str: string) => {
  return str.toLowerCase().replace(/_/g, ' ').replace(/(?:^\w|[A-Z]|\b\w)/g, function(word) {
    return word.toUpperCase();
  });
};

export const fixFirstLetterToUpper = (str: string) =>{
  return str.split(' ').map((string) => string[0].toUpperCase() ).join('');
};

export function tail (arr: unknown[] ){
  return arr[arr.length-1];
}

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};


export const findIdFromUrl = (path: string) => {
  const [idFromUrl, setIdFromUrl] = useState('');
  const location = useLocation();

  useEffect(() => {
    if(location.pathname?.includes(path)) {
      setIdFromUrl(location?.pathname.split('/')[2]);
    }
  },[]);

  return idFromUrl;
};

export const downloadUserImage = async (imgUrl: string) => {
  if(imgUrl){
    return  await  Api<null,Response>().get(imgUrl)
      .then(async res => {
        return {blob: URL.createObjectURL(await res.blob()), imgUrl: imgUrl};
      });
  }
  else{
    return {blob: '', imgUrl: imgUrl};
  }
};
export const blobCreator = (data: any |  Record<string, string>[]) => {
  return new Blob([JSON.stringify(data)], { type: 'application/json' });
};


export const getDifference = (endDate: string, startDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return differenceInCalendarDays(end, start) + 1;
};

export const FILE_SIZE_LIMIT = 10485760;
export const FILE_QUANTITY_LIMIT = 20;

export const checkFile = async (size: number, name: string) => {
  const fileExtension = '.' + name.split('.').pop()?.toLowerCase();
  const {areExtensionsFetched, allowedExtensions, fetchAllowedExtensions} = RequestsStore;
  if (!areExtensionsFetched) {
    await fetchAllowedExtensions();
  }
  const correctType = !!fileExtension && allowedExtensions.includes(fileExtension);
  
  if (!size) {
    return correctType;
  }

  return correctType && size < FILE_SIZE_LIMIT && name.length < 127;
};

export const downloadTemplateByEnum = (data: DownloadTemplate) => {
  const {downloadTemplateForRequestByEnum} = TemplatesStore;
  return downloadTemplateForRequestByEnum(data);
};

export const submitLeaveRequestFields = (payload: any, formData: FormData, isValidate = true, fromPlanned = false, plannedPayload?: any ) => {
  const blob = blobCreator(payload);

  formData.append('leaveRequest', blob,'leaveRequest.json');

  if(fromPlanned){
    const plannedBlob = blobCreator(plannedPayload);
    formData.append('plannedLeaveRequest', plannedBlob,'plannedLeaveRequest.json');
  }

  const url = `${process.env.REACT_APP_LM_API_URL}/api`;


  return Api<FormData, LeaveRequestResponseI>().post(!isValidate ?
    `${url}/v2/leaverequest`
    : `${url}/v1/leaverequest/validation`, formData, true);
};

const addZeroToTime = (num:number) =>{
  return num < 10 ? '0' + num : num;
};

export const getTimeFormatDate = ((date:Date) =>{
  return `${addZeroToTime(getHours(date))}:${addZeroToTime(getMinutes(date))}`;
});

export const parseAndChangeDate = (item:LeavesAll) =>{
  const chooseDate = item.periodDuration?.split(' ')[0].split('-')[0];
  if(!chooseDate){
    return new Date(0);
  }
  const startDateOrYearStr: string = chooseDate.toString(); // 'yyyy' year or 'dd.mm.yyyy'
  if (startDateOrYearStr.includes('.')) { // is  date 'dd.mm.yyyy'
    const [dayStr, monthStr, yearStr] = startDateOrYearStr.split('.');
    return new Date(yearStr + '-'+ monthStr + '-' + dayStr);
  }else{
    return new Date(startDateOrYearStr+'-01-01');
  }
};

export const sendUsageRequest = (page: string) => {
  apiConfig.statisticsUsageService.sendUsageRequest(page);
};

export const sendUsageRequestWithBody = (page: string, body: number[]) => {
  apiConfig.statisticsUsageService.sendUsageRequestWithBody(page, body);
};

export const shortenStr = (str: string, maxLength: number) => {
  if (str.length <= maxLength) {
    return str;
  }
  const ending = '...';
  return str.slice(0, maxLength - ending.length) + ending;
};

export const isFirefox = () => {
  return !!navigator.userAgent.match(/firefox|fxios/i);
};